import { ShopRegion, SupportedLocaleIdentifier, supportedRegions } from '@syconium/weeping-figs'

// Be careful. This map would persist serverside and be shared across all SSRs.
// But creating a regex or a number formatter is expensive. So lets avoid doing
// it everytime a function is called by leveraging these shared const variables.
const currencyFormattersByLocaleByCurrency: Record<string, Record<string, Intl.NumberFormat>> = {}
const maxFractionDigitsByLocaleByCurrency: Record<string, Record<string, number>> = {}
const trailingUsdZerosRegex = /\.00$/

interface GetDollarsAndCentsForAmountProps {
  value: number
  unit: 'cents' | 'dollars'
  currency: string
  locale: string
}

export const getDollarsAndCentsForAmount = ({
  value,
  unit,
  currency,
  locale,
}: GetDollarsAndCentsForAmountProps) => {
  // Check if we already have the maximumFractionDigits cached
  let maximumFractionDigits = maxFractionDigitsByLocaleByCurrency[locale]?.[currency]
  if (!maximumFractionDigits) {
    // Only create temp formatter if we don't have the value cached
    const tempFormatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    })
    maximumFractionDigits = tempFormatter.resolvedOptions().maximumFractionDigits ?? 2
    maxFractionDigitsByLocaleByCurrency[locale] = maxFractionDigitsByLocaleByCurrency[locale] ?? {}
    maxFractionDigitsByLocaleByCurrency[locale]![currency] = maximumFractionDigits
  }

  const centsPerDollar = Math.pow(10, maximumFractionDigits ?? 2)

  return {
    cents: unit === 'cents' ? value : value * centsPerDollar,
    dollars: unit === 'dollars' ? value : value / centsPerDollar,
    maximumFractionDigits,
  }
}

interface FormatPrettyCurrencyProps {
  value: number
  unit: 'cents' | 'dollars'
  currency: string
  locale: string
  truncateTrailingZeros?: boolean
  explicitFormat?: boolean
}

export function formatPrettyCurrency({
  value,
  unit,
  currency,
  locale,
  truncateTrailingZeros,
  explicitFormat,
}: FormatPrettyCurrencyProps): string {
  if (currency === 'USD') {
    locale = 'en-US'
    explicitFormat = false
  } else if (currency === 'CHF') {
    explicitFormat = false
  }

  const { maximumFractionDigits, dollars } = getDollarsAndCentsForAmount({
    value,
    unit,
    currency,
    locale,
  })

  // These are expensive to create in memory for every price value on the screen.
  // Reuse them if we have already made them.
  const numberFormatter =
    currencyFormattersByLocaleByCurrency[locale]?.[currency] ??
    new Intl.NumberFormat(locale, {
      currency,
      maximumFractionDigits: Math.max(2, maximumFractionDigits),
      minimumFractionDigits: Math.min(2, maximumFractionDigits),
      style: 'currency',
      currencyDisplay: 'narrowSymbol',
    })

  const prettyCurrency: string = numberFormatter.format(dollars)

  let formattedCurrency = prettyCurrency

  // Add it if it was missing.
  if (currencyFormattersByLocaleByCurrency[locale]?.[currency] === undefined) {
    currencyFormattersByLocaleByCurrency[locale] =
      currencyFormattersByLocaleByCurrency[locale] ?? {}
    currencyFormattersByLocaleByCurrency[locale]![currency] = numberFormatter
  }

  if (truncateTrailingZeros) formattedCurrency = prettyCurrency.replace(trailingUsdZerosRegex, '')
  if (explicitFormat) formattedCurrency = `${formattedCurrency} ${currency}`

  return formattedCurrency
}

type IntlPrettyCurrencyProps = {
  value: number | undefined
  unit: 'cents' | 'dollars'
  region: ShopRegion
  locale: SupportedLocaleIdentifier
  truncateTrailingZeros?: boolean
  explicitFormat?: boolean
}

export const intlPrettyCurrency = ({
  value,
  unit,
  region,
  locale,
  truncateTrailingZeros = false,
  explicitFormat = false,
}: IntlPrettyCurrencyProps): string => {
  const computedCurrency = region?.currency ?? supportedRegions.US.currency
  const computedLocale = locale ?? supportedRegions.US.defaultLocale

  if (value === undefined) {
    return ''
  }

  return formatPrettyCurrency({
    value,
    unit,
    currency: computedCurrency,
    locale: computedLocale,
    truncateTrailingZeros,
    explicitFormat,
  })
}
